<!-- @format -->

<template>
  <div>
    <div class="ui attached segment">
      <strong style="font-size: large">Retirement goals</strong>
    </div>
    <div
      :class="'ui ' + (loading ? ' loading ' : '') + ' attached segment'"
      style="padding: 20px"
    >
      <div class="row">
        <div class="sixteen wide column">
          <div class="ui attached message">
            <strong style="font-size: x-large">What is your target? </strong>
          </div>

          <StatCard
            style="border-left: 1px solid #d4d4d5;border-right: 1px solid #d4d4d5;"
            :goal1="{
              header: 'Your Target',
              value: 'R' + formatNr(raSummary.target, '0,0.0a'),
              footer: 'in today s terms',
            }"
            :goal2="{
              header: 'Monthly Income',
              value: 'R' + formatNr(raSummary.finalYearSalary / 12, '0,0.0a'),
              footer: 'at retirement',
            }"
          />
          <div class="ui attached segment" style="border-top: none">
            <p style="padding-top: 10px">
              A portfolio value of
              <strong>R{{ formatNr(raSummary.target, "0,0.0a") }}</strong> will
              give you a high likelihood of drawing an income of
              <strong
                >R{{
                  formatNr(raSummary.finalYearSalary / 12, "0,0.0a")
                }}</strong
              >
              per month. These figures are in today's terms. A monthly income of
              <strong
                >R{{
                  formatNr(raSummary.finalYearSalary / 12, "0,0.0a")
                }}</strong
              >
              is based on
              <strong>{{ raSummary.raOutputs[0].targetIncomeText }}</strong
              >. For this projection, we are assuming that your salary will grow
              at
              {{
                formatNr(raSummary.raInputsDto.salaryGrowthRate / 100, "0,0.0%")
              }}
              per annum on average until retirement at age
              {{ raSummary.retirementAge }}.
              <br />
            </p>
            <p>
              <small>
                See<a href="#" @click.prevent="showHowWeCalculateTarget = true">
                  here how we calculate your target.</a
                >
              </small>
            </p>
            <sui-modal v-model="showHowWeCalculateTarget">
              <sui-modal-header
                >How your target is calculated.</sui-modal-header
              >
              <sui-modal-content>
                <sui-modal-description>
                  <div class="ui message">
                    <strong
                      >How do we calculate your goal of R
                      {{ formatNr(raSummary.target, "0,0.0a") }}?</strong
                    >
                    Your target value is based on the purchase price of a
                    guaranteed annuity payable for life which will provide you
                    with your target salary of R{{
                      formatNr(raSummary.finalYearSalary / 12, "0,0.0a")
                    }}
                    per month with a 5% level annual increase at retirement. The
                    estimate value of the life annuity is based on an average
                    price provided by various life insurers using a 10-year
                    guaranteed annuity payable for life for a single male
                    retiring at age 60. The market rate for this type of annuity
                    is a good indicator to understand your target value. It will
                    be updated on an ad-hoc basis.
                  </div>
                </sui-modal-description>
              </sui-modal-content>

              <sui-modal-actions>
                <sui-button @click.native="showHowWeCalculateTarget = false"
                  >Close</sui-button
                >
              </sui-modal-actions>
            </sui-modal>
          </div>
        </div>
      </div>
      <div>
        <br />
        <div class="row">
          <div class="sixteen wide column">
            <ra-success-rate
              :retirementAnnuity="raSummary"
              :appearanceSettings="appearanceSettings"
              :clientName="clientName"
            />
          </div>
        </div>
      </div>
      <br />
      <div class="row">
        <div class="sixteen wide column">
          <div class="ui attached message">
            <strong style="font-size: x-large"
              >How much tax can you save?
            </strong>
          </div>

          <div class="ui attached segment" style="border-top: none">
            <p style="padding-top: 10px">
              Retirement funds provide you with a tax efficient way to save for
              retirement.
            </p>
            <h3>A. Contributions are tax deductible</h3>
            <p>
              Contributions to a retirement fund are tax deductible - subject to
              a limit of 27.5% of your income to a maximum of R350k. This allows
              you to pay less tax throughout your working life.
            </p>

            <StatCard
              v-if="false"
              style="border-left: 1px solid #d4d4d5;border-right: 1px solid #d4d4d5;"
              :goal1="{
                header: '',
                value:
                  'R' +
                  formatNr(
                    raSummary.getRaTaxInfoResponse.taxPayableVol -
                      raSummary.getRaTaxInfoResponse.taxPayable,
                    '0,0.0a'
                  ),
                footer: ' tax saving ',
              }"
              :goal2="{
                header: raSummary.raOutputs[0].strategyDescription,
                value:
                  'R' +
                  formatNr(raSummary.getRaTaxInfoResponse.taxPayable, '0,0.0a'),
                footer: 'tax payable',
              }"
              :goal3="{
                header: 'No contributions to RA',
                value:
                  'R' +
                  formatNr(
                    raSummary.getRaTaxInfoResponse.taxPayableVol,
                    '0,0.0a'
                  ),
                footer: 'tax payable',
              }"
            />

            <table class="ui table">
              <thead>
                <tr>
                  <th></th>
                  <th style="text-align: center; " colspan="2">
                    Retirement Fund
                  </th>
                </tr>
                <tr>
                  <th>Illustrative 2021/22 Tax income statement</th>
                  <th style="text-align: center">
                    No
                  </th>
                  <th style="text-align: center">Yes</th>
                </tr>
              </thead>
              <tr>
                <td style="font-weight: bold">
                  Salary
                  <span
                    v-if="
                      raSummary.raInputsDto.annualBonusAsAPercentageOfSalary > 0
                    "
                  >
                    +
                    {{
                      formatNr(
                        raSummary.raInputsDto.annualBonusAsAPercentageOfSalary,
                        "0,0.0"
                      )
                    }}% annual bonus</span
                  >
                </td>
                <td style="text-align: center;font-weight: bold">
                  R{{
                    formatNr(raSummary.getRaTaxInfoResponse.income, "0,0.0a")
                  }}
                </td>
                <td style="text-align: center;font-weight: bold">
                  R{{
                    formatNr(raSummary.getRaTaxInfoResponse.income, "0,0.0a")
                  }}
                </td>
              </tr>

              <tr>
                <td>less retirement fund contributions</td>
                <td style="text-align: center; border-bottom: solid 1px black;">
                  -
                </td>
                <td style="text-align: center; border-bottom: solid 1px black;">
                  R{{
                    formatNr(
                      raSummary.getRaTaxInfoResponse.pensionFundContributions,
                      "0,0.0a"
                    )
                  }}
                </td>
              </tr>
              <tr>
                <td>Taxable income</td>
                <td style="text-align: center; border-bottom: solid 1px black;">
                  R{{
                    formatNr(raSummary.getRaTaxInfoResponse.income, "0,0.0a")
                  }}
                </td>
                <td
                  style="text-align: center;; border-bottom: solid 1px black;"
                >
                  R{{
                    formatNr(
                      raSummary.getRaTaxInfoResponse.income -
                        raSummary.getRaTaxInfoResponse.pensionFundContributions,
                      "0,0.0a"
                    )
                  }}<i class="ui icon arrow down"></i>
                </td>
              </tr>
              <tr class="highlightrow">
                <td>Income Tax Payable*</td>

                <td style="text-align: center; border-bottom: double;">
                  R{{
                    formatNr(
                      raSummary.getRaTaxInfoResponse.taxPayableVol,
                      "0,0.0a"
                    )
                  }}
                </td>
                <td style="text-align: center; border-bottom: double;">
                  R{{
                    formatNr(
                      raSummary.getRaTaxInfoResponse.taxPayable,
                      "0,0.0a"
                    )
                  }}
                  <i class="ui icon arrow down"></i>
                </td>
              </tr>
              <tfoot>
                <tr>
                  <th colspan="3">
                    <small
                      >* This is an estimate for illustrative purposes. It is
                      based on the latest tax tables using your salary as sole
                      income. Other income sources - such as rental properties,
                      interest received -, medical aid contributions or
                      donations are not taken into account for this
                      illustration.
                    </small>
                  </th>
                </tr>
              </tfoot>
            </table>

            <p style="line-height: 150%">
              The above table illustrates the amount of income tax you can save
              due to contributions to a retirement fund. In your case, an annual
              salary of
              <span class="highlight"
                >R{{
                  formatNr(raSummary.raInputsDto.currentSalary, "0,0.0a")
                }}</span
              >

              <span
                class="highlight"
                v-if="
                  raSummary.raInputsDto.annualBonusAsAPercentageOfSalary > 0
                "
              >
                and a bonus equivalent to
                {{
                  formatNr(
                    raSummary.raInputsDto.annualBonusAsAPercentageOfSalary,
                    "0,0.0"
                  )
                }}% of your salary</span
              >
              would result in estimated taxes of R
              {{
                formatNr(raSummary.getRaTaxInfoResponse.taxPayableVol, "0,0.0a")
              }}
              for the current tax year. Since contributions are tax deductible,
              your tax payable is reduced to
              <span class="highlight">
                R{{
                  formatNr(raSummary.getRaTaxInfoResponse.taxPayable, "0,0.0a")
                }}</span
              >. This represents a
              <span class="highlight"
                >R{{
                  formatNr(
                    raSummary.getRaTaxInfoResponse.taxPayableVol -
                      raSummary.getRaTaxInfoResponse.taxPayable,
                    "0,0.0a"
                  )
                }}</span
              >
              tax saving for the current 2021/22 tax year. *
            </p>
            <h3>B. Growth is tax free until retirement</h3>
            <p>
              No tax is applied to capital gains, dividends or interest
              throughout your working life.
            </p>
            <p style="color: orange">
              TO DO USING ESTIMATED YIELDS ETC. Calculate amonut of tax payable
              on interest, divs and compare to a discretionary vehicle.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as _ from "lodash";
import numeral from "numeral";
import RaSuccessRate from "@/components/v3/RaSuccessRate.vue";
import StatCard from "../StatCard.vue";
export default {
  name: "Testing",
  components: { RaSuccessRate, StatCard },
  props: ["raSummary", "clientName", "appearanceSettings", "loading"],
  data() {
    // 2.326 -- 99th percentile
    // 1.645 - 95th percentile
    // 1.282 - 90th percentile
    // 1.6449 - 85th percentile
    // 0.674 - 75h percentile
    // 0.8416 - 60th percenetile
    // 0.125 - 55th percentile
    return { showHowWeCalculateTarget: false };
  },
  computed: {
    partnerId() {
      return this.$store.state.partnerId;
    },
  },
  methods: {
    formatNr(val, fmt) {
      if (fmt) {
        return numeral(val).format(fmt);
      } else return numeral(val).format("0,0");
    },

    async init() {},
  },
  mounted() {
    this.init();
  },
};
</script>
<style scoped>
.highlight {
  font-weight: bold;
}

.highlightrow > td {
  font-weight: bold;
}

.selecthover tr {
  cursor: pointer;
}
</style>
